import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { formatExpiry } from "../../Helpers/SuppressionDuration";

/**
 * Task 9 — C4: confirm before an existing suppression is overwritten.
 *
 * The client's answer was "treat it as an amendment but prompt the customer to ask if they want to
 * overwrite the existing configuration". So re-suppressing something already suppressed is allowed,
 * but never silent: each affected job is named with the expiry that would be replaced.
 *
 * Follows ConfirmSuppressionRemovalModal in shape. It is deliberately a dialog rather than a
 * window.confirm, because the customer needs to see which jobs and which dates before deciding —
 * that is the whole point of the prompt.
 */
export default function ConfirmOverwriteModal({
    show,
    errorId,
    /** [{ jobId, jobName, currentExpiry }] — only the jobs that already have this error suppressed. */
    overwrites,
    /** The expiry every selected job would be given, or null for indefinite. */
    newExpiry,
    busy,
    onConfirm,
    onClose,
}) {
    const rows = overwrites || [];

    return (
        <Modal isOpen={show} toggle={onClose} backdrop="static" centered size="lg">
            <ModalHeader>
                <span style={{ fontSize: 18 }}>Overwrite existing suppression?</span>
            </ModalHeader>
            <ModalBody>
                <p>
                    {rows.length === 1 ? "This job already has " : "These jobs already have "}
                    <strong>{errorId}</strong> suppressed. Continuing replaces the existing expiry
                    with the new one.
                </p>

                <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                        <thead>
                            <tr>
                                <th>Job</th>
                                <th style={{ width: 150 }}>Currently until</th>
                                <th style={{ width: 150 }}>Would become</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.jobId}>
                                    <td>{row.jobName}</td>
                                    <td>{formatExpiry(row.currentExpiry)}</td>
                                    <td className="fw-semibold">{formatExpiry(newExpiry)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <p className="text-muted mb-0" style={{ marginTop: 12, fontSize: 13 }}>
                    Any other jobs you selected are suppressed as normal. Already-processed backup
                    sessions are not affected.
                </p>
            </ModalBody>
            <ModalFooter>
                <button className="btn btn-primary" onClick={onConfirm} disabled={busy}>
                    {busy ? "Saving…" : "Overwrite"}
                </button>
                <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
                    Cancel
                </button>
            </ModalFooter>
        </Modal>
    );
}
